import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { GISMap } from "@/components/GISMap";
import { toast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Loader2, Play } from "lucide-react";

const SAMPLE_POLYGON = [
  [30.98, -17.82], [31.06, -17.82], [31.06, -17.88], [30.98, -17.88], [30.98, -17.82]
];

export const SpatialValidation = () => {
  const [text, setText] = useState(JSON.stringify(SAMPLE_POLYGON, null, 2));
  const [check, setCheck] = useState("both");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [poly, setPoly] = useState(SAMPLE_POLYGON);

  const validate = async () => {
    setBusy(true); setReport(null);
    let coords;
    try { coords = JSON.parse(text); } catch { toast.error("Invalid JSON polygon"); setBusy(false); return; }
    try {
      const { data } = await api.post("/spatial/validate", { coordinates: coords, check_against: check });
      setReport(data);
      setPoly(coords);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const badge = (level) => {
    if (level === "blocking") return <span className="cad-chip cad-chip-red">BLOCKING</span>;
    if (level === "advisory") return <span className="cad-chip cad-chip-gold">ADVISORY</span>;
    return <span className="cad-chip">INFO</span>;
  };

  return (
    <div className="space-y-6" data-testid="page-spatial-validation">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Enterprise Spatial Validation Engine</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Polygon Topology & Conflict Analysis</h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1">Real polygon intersection powered by Shapely · protected-area, road-reserve and mineral-right conflict detection.</div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Polygon input (GeoJSON coordinates)</div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="spatial-input" rows={10}
                    className="mt-2 w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[12px] font-mono-cad text-[#F6F8FA]" />
          <div className="mt-3 flex items-center gap-2">
            <select value={check} onChange={(e) => setCheck(e.target.value)} data-testid="spatial-check"
                    className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[12px] text-[#F6F8FA]">
              <option value="both">Parcels + Mining</option>
              <option value="parcels">Parcels only</option>
              <option value="mineral_rights">Mining only</option>
            </select>
            <button onClick={validate} disabled={busy} data-testid="spatial-run"
                    className="cad-btn-primary px-4 py-2 rounded-sm text-[13px] inline-flex items-center gap-1.5">
              {busy ? <Loader2 size={13} className="animate-spin"/> : <Play size={13}/>} Validate
            </button>
          </div>
          <div className="mt-3 text-[10px] font-mono-cad uppercase tracking-widest text-[#7C93A5]">
            Format: array of [longitude, latitude] pairs. Close the ring by repeating the first point.
          </div>
        </div>

        <div className="lg:col-span-2">
          <GISMap
            parcels={[{ id: "input", parcel_number: "TEST", coordinates: poly, province: "Input",
                        district: "—", land_type: "test", area_ha: report?.area_ha || 0 }]}
            height={340}
          />
        </div>
      </div>

      {report && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="cad-panel p-5">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Topology</div>
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[13px] text-[#DCE3E8]">Valid geometry</span>
                {report.valid_geometry ? <CheckCircle2 className="text-[#6BB88A]" size={16}/> : <XCircle className="text-[#E67E7E]" size={16}/>}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[13px] text-[#DCE3E8]">Self-intersects</span>
                {report.self_intersects ? <XCircle className="text-[#E67E7E]" size={16}/> : <CheckCircle2 className="text-[#6BB88A]" size={16}/>}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[13px] text-[#DCE3E8]">Area</span>
                <span className="font-mono-cad text-[#F6F8FA]">{report.area_ha} ha</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[13px] text-[#DCE3E8]">Topology score</span>
                <span className="font-mono-cad text-[#F6F8FA]">{report.topology_score}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[13px] text-[#DCE3E8]">Quality score</span>
                <span className="font-mono-cad" style={{
                  color: report.quality_score >= 75 ? "#6BB88A" : report.quality_score >= 40 ? "#C99A2E" : "#E67E7E",
                }}>{report.quality_score}</span>
              </div>
            </div>
          </div>

          <div className="cad-panel p-5 lg:col-span-2">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Findings</div>
            <div className="mt-3 space-y-2" data-testid="spatial-findings">
              {report.issues.length === 0 && report.warnings.length === 0 && report.conflicts.length === 0 && report.protected_intersections.length === 0 && (
                <div className="text-[13px] text-[#6BB88A] flex items-center gap-2"><CheckCircle2 size={16}/> No conflicts detected. Polygon is clear.</div>
              )}
              {report.issues.map((i, k) => (
                <div key={k} className="flex items-start gap-2 border-l-2 border-[#A93F3F] pl-3 py-1">
                  {badge(i.level)}
                  <div><div className="text-[13px] text-[#F6F8FA]">{i.code.replace(/_/g, " ")}</div><div className="text-[12px] text-[#B3C2CC]">{i.detail}</div></div>
                </div>
              ))}
              {report.warnings.map((i, k) => (
                <div key={k} className="flex items-start gap-2 border-l-2 border-[#C99A2E] pl-3 py-1">
                  {badge(i.level)}
                  <div><div className="text-[13px] text-[#F6F8FA]">{i.code.replace(/_/g, " ")}</div><div className="text-[12px] text-[#B3C2CC]">{i.detail}</div></div>
                </div>
              ))}
              {report.protected_intersections.map((p, k) => (
                <div key={k} className="flex items-start gap-2 border-l-2 border-[#7CBFDC] pl-3 py-1">
                  <AlertTriangle size={14} className="text-[#7CBFDC] mt-0.5"/>
                  <div>
                    <div className="text-[13px] text-[#F6F8FA]">{p.name}</div>
                    <div className="text-[12px] text-[#B3C2CC]">{p.kind.replace(/_/g, " ")} · overlap {p.overlap_area_ha} ha</div>
                  </div>
                </div>
              ))}
              {report.conflicts.map((c, k) => (
                <div key={k} className="flex items-start gap-2 border-l-2 border-[#E67E7E] pl-3 py-1">
                  <XCircle size={14} className="text-[#E67E7E] mt-0.5"/>
                  <div>
                    <div className="text-[13px] text-[#F6F8FA] font-mono-cad">{c.reference}</div>
                    <div className="text-[12px] text-[#B3C2CC]">{c.collection.replace(/_/g, " ")} · overlap {c.overlap_area_ha} ha · {c.province}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
